/**
 * Company: SPHMedia
 * Description: [Fetches reaction list for provided article ids]
 */

import { SHOW_REACTIONS } from "@config/constants";
import fetchBHApi from "@helper/fetchBHApi";
import {
  EmptyReactionsResponse,
  ReactionsListResponse,
  ReactionsResponse,
} from "@typings/Reactions";
import { useCallback, useEffect, useRef, useState } from "react";

type useReactionListData = {
  data: ReactionsListResponse | null;
  isLoading: boolean;
  error: Error | null;
};

type useReactionListProps = {
  articleIds: string[];
};

/**
 * Custom React Hook: useReactionListHook
 * Description: [Calls Reaction API for articles]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useReactionList({
  articleIds,
}: useReactionListProps): useReactionListData {
  const [data, setData] = useState<ReactionsListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Track already fetched article IDs
  const fetchedIdsRef = useRef<Set<string>>(new Set());
  // Also track inflight IDs to prevent double requests
  const inflightIdsRef = useRef<Set<string>>(new Set());

  /**
   * Fetch reactions for given article IDs.
   */
  const fetchReactions = useCallback(async (ids: string[]) => {
    if (!ids.length || !SHOW_REACTIONS) return;

    setIsLoading(true);
    try {
      const idsParam = ids.join(",");
      const response = await fetchBHApi<
        ReactionsResponse<ReactionsListResponse> | EmptyReactionsResponse
      >(`reactions/get-reaction-list?articleIds=${idsParam}`, "GET");

      const reactionsData = response.data ?? null;

      if (reactionsData) {
        // Merge newly fetched reactions with cached ones
        setData((prev) => ({ ...prev, ...reactionsData }));
        ids.forEach((id) => fetchedIdsRef.current.add(id)); // mark fetched
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      ids.forEach((id) => inflightIdsRef.current.delete(id)); // clear inflight
      setIsLoading(false);
    }
  }, []);

  /**
   * Detect unseen article IDs and fetch their reactions.
   */
  useEffect(() => {
    if (!SHOW_REACTIONS || !articleIds.length) return;

    const unseenIds = articleIds.filter(
      (id) => !fetchedIdsRef.current.has(id) && !inflightIdsRef.current.has(id),
    );

    if (unseenIds.length > 0) {
      unseenIds.forEach((id) => inflightIdsRef.current.add(id)); // mark inflight
      void fetchReactions(unseenIds);
    }
  }, [articleIds, fetchReactions]);

  return { data, isLoading, error };
}
